import { canSlide } from './generator';
import type { ArrowData, Level } from './types';

const arrows: ArrowData[] = [
  { id: 'first', direction: 'UP', cells: [{ x: 1, y: 3 }, { x: 1, y: 2 }, { x: 1, y: 1 }] },
  { id: 'blocked', direction: 'RIGHT', cells: [{ x: 2, y: 5 }, { x: 3, y: 5 }, { x: 4, y: 5 }] },
  { id: 'blocker', direction: 'LEFT', cells: [{ x: 6, y: 5 }, { x: 6, y: 4 }, { x: 6, y: 3 }, { x: 5, y: 3 }] },
  { id: 'last', direction: 'UP', cells: [{ x: 3, y: 8 }, { x: 4, y: 8 }, { x: 4, y: 7 }] }
];

export const TUTORIAL_LEVEL: Level = {
  id: 'practice', number: 0, seed: 0, generatorVersion: 6, shape: 'custom',
  width: 8, height: 10, difficulty: 1, timeLimit: 0,
  arrows, active: arrows.flatMap(arrow => arrow.cells)
};

export const TUTORIAL_STEPS = [
  { title: 'Tap an arrow', copy: 'Arrows move where their tip points. Tap the glowing arrow to send it off the board.', target: 'first', action: 'tap' },
  { title: 'Look before you tap', copy: 'This arrow has another arrow in its way. Tap it to see what happens—practice is safe.', target: 'blocked', action: 'tap' },
  { title: 'Clear the way', copy: 'Blocked! In a level, that costs a heart. Remove the glowing arrow that is blocking the path.', target: 'blocker', action: 'tap' },
  { title: 'Now it can escape', copy: 'The whole path to the edge must be clear, even across gaps. Tap the arrow you just freed.', target: 'blocked', action: 'tap' },
  { title: 'Need a nudge?', copy: 'Tap the lightbulb below to find a safe move. Your practice hint is free.', target: null, action: 'hint' },
  { title: 'Follow the hint', copy: 'A hint highlights an arrow with a clear path. Tap the gold arrow to finish your practice board.', target: 'last', action: 'tap' }
] as const;

/** Practice uses real blocking rules, but never creates a paid/timed attempt. */
export class TutorialLesson {
  step = 0;
  readonly remainingIds = new Set(arrows.map(arrow => arrow.id));
  get current() { return TUTORIAL_STEPS[this.step]; }
  get complete(): boolean { return this.step === TUTORIAL_STEPS.length; }

  tap(id: string): 'ignored' | 'blocked' | 'cleared' {
    if (this.complete || this.current!.action !== 'tap' || id !== this.current!.target || !this.remainingIds.has(id)) return 'ignored';
    const arrow = arrows.find(arrow => arrow.id === id)!;
    const occupied = new Set(arrows.filter(arrow => this.remainingIds.has(arrow.id)).flatMap(arrow => arrow.cells.map(cell => `${cell.x},${cell.y}`)));
    const clear = canSlide(arrow, new Set(TUTORIAL_LEVEL.active.map(cell => `${cell.x},${cell.y}`)), occupied);
    if (clear) this.remainingIds.delete(id);
    this.step++;
    return clear ? 'cleared' : 'blocked';
  }

  useHint(): boolean {
    if (this.complete || this.current!.action !== 'hint') return false;
    this.step++;
    return true;
  }
}
